import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Code, Compass, Lightbulb, Rocket, Target, Users, Clock, Layers, BarChart3, Zap, Bot, Globe, Search, FileText, Database, Landmark, Hash, ListTree, Calendar, FolderOpen, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/seo/SEOHead";

const LandingPage = () => {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });

  // Track mouse position for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <SEOHead
        title="LaunchGuide — The AI Marketing Engine for Vibe Coders"
        description="LaunchGuide is a platform for vibe coders with no clue of marketing. From code to $10k MRR with a proven step-by-step plan and automated AI Agents."
        pathname="/landing"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative isolate py-12 md:py-16 lg:py-20 overflow-hidden">
        {/* Enhanced animated gradient background */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${JSON.stringify(`${import.meta.env.BASE_URL}hero-night.png`)})`,
            }}
            aria-hidden
          />
          <div
            className="absolute inset-0 bg-gradient-to-b from-background/35 via-background/18 to-background/42 dark:from-zinc-950/40 dark:via-zinc-950/22 dark:to-zinc-950/48"
            aria-hidden
          />
          <div className="absolute inset-0" style={{
            background: "radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.25) 0%, rgba(59, 130, 246, 0.15) 30%, rgba(255, 255, 255, 0) 70%)"
          }}></div>
          
          {/* Animated spotlight following mouse position */}
          <motion.div 
            className="absolute w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
            style={{
              background: "radial-gradient(circle at center, rgba(79, 70, 229, 0.4) 0%, transparent 70%)",
              left: mousePosition.x - 250,
              top: mousePosition.y - 250,
            }}
            animate={{
              left: mousePosition.x - 250,
              top: mousePosition.y - 250,
            }}
            transition={{
              type: "spring",
              damping: 15,
              stiffness: 150,
            }}
          />
          
          {/* Enhanced animated particles - more visible */}
          <div className="absolute inset-0">
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-blue-400/40 dark:bg-blue-400/50"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: `${Math.random() * 8 + 4}px`,
                  height: `${Math.random() * 8 + 4}px`,
                }}
                animate={{
                  y: [0, Math.random() * 100 - 50],
                  opacity: [0.3, 0.9, 0.3],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 5,
                }}
              />
            ))}
          </div>
        </div>
        
        {/* Enhanced animated grid pattern */}
        <div className="absolute inset-0 z-0 opacity-[0.05] dark:opacity-[0.1]">
          <div className="absolute inset-0" style={{
          backgroundImage: "linear-gradient(90deg, rgba(99, 102, 241, 0.5) 1px, transparent 1px), linear-gradient(rgba(99, 102, 241, 0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px"
          }}></div>
        </div>
        
        {/* Enhanced floating elements - more visible */}
        <motion.div 
          className="absolute top-[10%] right-[20%] w-80 h-80 rounded-full bg-blue-400/10 dark:bg-blue-400/20 blur-3xl" 
          animate={{
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.7, 0.4]
          }} 
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }} 
        />
        
        <motion.div 
          className="absolute bottom-[20%] left-[15%] w-96 h-96 rounded-full bg-blue-400/10 dark:bg-blue-400/20 blur-3xl" 
          animate={{
            y: [0, -40, 0],
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5]
          }} 
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }} 
        />
        
        <div className="container relative z-10 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Staggered animation for hero content */}
            <motion.div className="flex flex-col items-center text-center space-y-6" initial="hidden" animate="visible" variants={{
            hidden: {
              opacity: 0
            },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.2
              }
            }
          }}>
              {/* Badge */}
              <motion.div variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6
                }
              }
            }} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">#1 Marketing Tool for Vibe Coders</span>
              </motion.div>
              
              {/* Main headline */}
              <motion.h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight" variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7
                }
              }
            }}>
                <span>The </span>
                <span className="relative">
                  <span className="gradient-text">
                    AI Marketing Engine
                  </span>
                  <motion.svg className="absolute -bottom-2 left-0 w-full h-[0.6em] -z-10 text-blue-200 dark:text-blue-900/50" viewBox="0 0 300 30" fill="none" initial={{
                  pathLength: 0
                }} animate={{
                  pathLength: 1
                }} transition={{
                  delay: 1,
                  duration: 1.5,
                  ease: "easeInOut"
                }}>
                    <path d="M5 22.5C5 22.5 40 5 75 5C110 5 145 22.5 180 22.5C215 22.5 250 5 295 5" stroke="currentColor" strokeWidth="10" strokeLinecap="round" strokeDasharray="1 1" />
                  </motion.svg>
                </span>
                <span> for Vibe Coders</span>
              </motion.h1>
              
              {/* Subheadline */}
              <motion.p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-300 max-w-3xl mx-auto" variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7
                }
              }
            }}>LaunchGuide is a platform for vibe coders with no clue of marketing.
From code to $10k MRR with a proven step-by-step plan and automated AI Agents.</motion.p>
              
              {/* CTA buttons */}
              <motion.div className="flex flex-col sm:flex-row gap-4 mt-1" variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7
                }
              }
            }}>
                <Button size="lg" className="relative group overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12" asChild>
                  <Link to="/auth/register">
                    <span className="relative z-10 flex items-center">
                      Get Started <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                    <span className="spotlight" style={{
                    '--x': `${mousePosition.x}px`,
                    '--y': `${mousePosition.y}px`
                  } as React.CSSProperties}></span>
                  </Link>
                </Button>
                
                <Button size="lg" variant="outline" className="border-zinc-200 text-zinc-800 dark:text-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 px-8 h-12" asChild>
                  <Link to="/features">
                    Explore Features
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
            
            {/* Hero image/dashboard preview - moved higher */}
            <motion.div className="mt-10 md:mt-14 relative" initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }}>
              <div className="relative">
                {/* Glow effect - enhanced */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-30 blur-xl"></div>
                
                {/* Dashboard preview */}
                <div className="relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-zinc-100/80 to-white/90 dark:from-zinc-900/80 dark:to-zinc-800/90 backdrop-blur-sm z-10 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <Button size="lg" className="bg-white text-zinc-900 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-white dark:hover:bg-zinc-700" asChild>
                      <Link to="/features">
                        Explore Features
                      </Link>
                    </Button>
                  </div>
                  
                  <img src="/images/dashboard-preview.svg" alt="LaunchGuide Dashboard" className="w-full h-auto" onError={e => {
                  e.currentTarget.src = "https://placehold.co/1200x675/f8fafc/64748b?text=LaunchGuide+Dashboard";
                }} />
                </div>
                
                {/* Floating feature highlights */}
                <motion.div className="absolute -left-6 top-1/4 bg-white dark:bg-zinc-800 rounded-lg shadow-lg p-3 flex items-center gap-3 border border-zinc-100 dark:border-zinc-700" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 1.2
              }}>
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-2">
                    <Target className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Digital Presence Analyzer</h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">Advanced analysis of your website, social profiles, and positioning</p>
                  </div>
                </motion.div>
                
                <motion.div className="absolute -right-6 top-1/3 bg-white dark:bg-zinc-800 rounded-lg shadow-lg p-3 flex items-center gap-3 border border-zinc-100 dark:border-zinc-700" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 1.4
              }}>
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-2">
                    <Bot className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Marketing Agents</h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">AI-powered user acquisition</p>
                  </div>
                </motion.div>
                
                <motion.div className="absolute -bottom-6 left-1/4 bg-white dark:bg-zinc-800 rounded-lg shadow-lg p-3 flex items-center gap-3 border border-zinc-100 dark:border-zinc-700" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 1.6
              }}>
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-2">
                    <Rocket className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Launch Strategy</h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">Step-by-step playbook</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      

      
      {/* Product Differentiators Section */}
      <section className="py-20 md:py-28 relative">
        {/* Enhanced background with gradient effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-900/30 dark:to-zinc-900/10 -z-10"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] -z-10 opacity-50 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        {/* Abstract shapes for visual interest */}
        <motion.div 
          className="absolute top-20 right-[5%] w-64 h-64 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-3xl" 
          animate={{
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3]
          }} 
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }} 
        />
        
        <motion.div 
          className="absolute bottom-40 left-[10%] w-80 h-80 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-3xl" 
          animate={{
            y: [0, -40, 0],
            scale: [1, 1.05, 1],
            opacity: [0.4, 0.6, 0.4]
          }} 
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }} 
        />
        
        <div className="container px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {/* Enhanced section header with 3D effect */}
            <div className="text-center mb-20">
              <motion.div 
                className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-4"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Zap className="w-4 h-4 mr-2" />
                From Code to Customers
              </motion.div>
              
              <motion.h2 
                className="text-4xl md:text-5xl font-bold mb-6" 
                initial={{
                  opacity: 0,
                  y: 20
                }} 
                whileInView={{
                  opacity: 1,
                  y: 0
                }} 
                viewport={{
                  once: true
                }} 
                transition={{
                  duration: 0.5
                }}
              >
                Why <span className="relative inline-block">
                  <span className="gradient-text">
                    LaunchGuide
                  </span>
                  <motion.div 
                    className="absolute -bottom-2 left-0 h-[4px] w-full bg-gradient-to-r from-blue-600 to-sky-500 rounded-full opacity-70"
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.3 }}
                  ></motion.div>
                  <motion.div 
                    className="absolute -bottom-2 left-0 h-[4px] w-full bg-gradient-to-r from-blue-600 to-sky-500 rounded-full blur-sm opacity-70"
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.3 }}
                  ></motion.div>
                </span>?
              </motion.h2>
              <motion.p 
                className="text-xl text-zinc-600 dark:text-zinc-300 max-w-3xl mx-auto" 
                initial={{
                  opacity: 0,
                  y: 20
                }} 
                whileInView={{
                  opacity: 1,
                  y: 0
                }} 
                viewport={{
                  once: true
                }} 
                transition={{
                  duration: 0.5,
                  delay: 0.1
                }}
              >
                The only marketing platform designed specifically for technical founders who want to focus on building, not marketing.
              </motion.p>
            </div>
            
            {/* Enhanced feature cards grid with hover effects */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Card 1: Growth Playbook */}
              <motion.div 
                className="group relative bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-zinc-200 dark:border-zinc-700 hover:shadow-xl transition-all duration-300 overflow-hidden" 
                initial={{
                  opacity: 0,
                  y: 20
                }} 
                whileInView={{
                  opacity: 1,
                  y: 0
                }} 
                viewport={{
                  once: true
                }} 
                transition={{
                  duration: 0.5,
                  delay: 0.1
                }} 
                whileHover={{
                  y: -8,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/10 dark:to-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                <div className="absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br from-blue-600/10 to-blue-600/10 rounded-full blur-xl group-hover:scale-150 transition-all duration-500 -z-10"></div>
                
                <div className="bg-blue-100 dark:bg-blue-900/50 rounded-xl p-4 inline-flex mb-6 group-hover:scale-110 transition-transform duration-300 border border-blue-200/50 dark:border-blue-700/30">
                  <Compass className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                
                <h3 className="text-2xl font-bold mb-3 text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">Growth Playbook</h3>
                <p className="text-zinc-600 dark:text-zinc-300 mb-6">Follow a structured journey from launch to $10k MRR with phase-specific strategies and clear milestones.</p>
                
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Visual roadmap with clear progression</span>
                  </li>
                  <li className="flex items-start">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Proven path from launch to profitability</span>
                  </li>
                  <li className="flex items-start">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Expert tips from marketing veterans</span>
                  </li>
                </ul>
                
                <Link to="/auth/register" className="flex items-center text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                  <span className="text-sm">Explore the Playbook</span>
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
              
              {/* Card 2: AI-Powered Content */}
              <motion.div 
                className="group relative bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-zinc-200 dark:border-zinc-700 hover:shadow-xl transition-all duration-300 overflow-hidden" 
                initial={{
                  opacity: 0,
                  y: 20
                }} 
                whileInView={{
                  opacity: 1,
                  y: 0
                }} 
                viewport={{
                  once: true
                }} 
                transition={{
                  duration: 0.5,
                  delay: 0.2
                }} 
                whileHover={{
                  y: -8,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/10 dark:to-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                <div className="absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br from-blue-600/10 to-blue-600/10 rounded-full blur-xl group-hover:scale-150 transition-all duration-500 -z-10"></div>
                
                <div className="bg-blue-100 dark:bg-blue-900/50 rounded-xl p-4 inline-flex mb-6 group-hover:scale-110 transition-transform duration-300 border border-blue-200/50 dark:border-blue-700/30">
                  <Zap className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                
                <h3 className="text-2xl font-bold mb-3 text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">AI-Powered Content</h3>
                <p className="text-zinc-600 dark:text-zinc-300 mb-6">Generate conversion optimised marketing content for any platform in minutes, not hours.</p>
                
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Multi-platform content creation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">x.com, linkedin, reddit, blogs and more</span>
                  </li>
                  <li className="flex items-start">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">High-performing content structures</span>
                  </li>
                </ul>
                
                <Link to="/auth/register" className="flex items-center text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                  <span className="text-sm">See Content Generation</span>
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
              
              {/* Card 3: AI Marketing Agents */}
              <motion.div 
                className="group relative bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-zinc-200 dark:border-zinc-700 hover:shadow-xl transition-all duration-300 overflow-hidden" 
                initial={{
                  opacity: 0,
                  y: 20
                }} 
                whileInView={{
                  opacity: 1,
                  y: 0
                }} 
                viewport={{
                  once: true
                }} 
                transition={{
                  duration: 0.5,
                  delay: 0.3
                }} 
                whileHover={{
                  y: -8,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-pink-50 dark:from-blue-900/10 dark:to-pink-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                <div className="absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br from-blue-600/10 to-pink-600/10 rounded-full blur-xl group-hover:scale-150 transition-all duration-500 -z-10"></div>
                
                <div className="bg-gradient-to-br from-blue-100 to-pink-100 dark:from-blue-900/50 dark:to-pink-900/50 rounded-xl p-4 inline-flex mb-6 group-hover:scale-110 transition-transform duration-300 border border-blue-200/50 dark:border-blue-700/30 relative overflow-hidden">
                  {/* Subtle shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <Bot className="h-8 w-8 text-blue-600 dark:text-blue-400 relative z-10" />
                </div>
                
                <h3 className="text-2xl font-bold mb-3 text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">AI Marketing Agents</h3>
                <p className="text-zinc-600 dark:text-zinc-300 mb-6">Autonomous AI workers that operate 24/7 to discover customers, engage authentically, and convert prospects while you focus on building.</p>
                
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <div className="rounded-full bg-gradient-to-br from-blue-100 to-pink-100 dark:from-blue-900/30 dark:to-pink-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Intelligent prospect identification</span>
                  </li>
                  <li className="flex items-start">
                    <div className="rounded-full bg-gradient-to-br from-blue-100 to-pink-100 dark:from-blue-900/30 dark:to-pink-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Automated authentic engagement</span>
                  </li>
                  <li className="flex items-start">
                    <div className="rounded-full bg-gradient-to-br from-blue-100 to-pink-100 dark:from-blue-900/30 dark:to-pink-900/30 p-1 mr-3 mt-0.5">
                      <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">Self-optimizing performance</span>
                  </li>
                </ul>
                
                <Link to="/auth/register" className="flex items-center text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                  <span className="text-sm">Meet Your AI Team</span>
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </div>
            
            {/* Stunning feature showcase with metrics */}
            <motion.div 
              className="mt-16 md:mt-24 p-1 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg" 
              initial={{
                opacity: 0,
                y: 30
              }} 
              whileInView={{
                opacity: 1,
                y: 0
              }} 
              viewport={{
                once: true
              }} 
              transition={{
                duration: 0.7,
                delay: 0.4
              }}
            >
              <div className="bg-white dark:bg-zinc-900 rounded-xl p-8 md:p-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="text-center">
                    <h3 className="text-5xl font-bold text-blue-600 dark:text-blue-400 mb-2">70%</h3>
                    <p className="text-zinc-700 dark:text-zinc-300">Less time spent on marketing activities</p>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-5xl font-bold text-blue-600 dark:text-blue-400 mb-2">3x</h3>
                    <p className="text-zinc-700 dark:text-zinc-300">More marketing channels covered</p>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-5xl font-bold text-blue-600 dark:text-blue-400 mb-2">40%</h3>
                    <p className="text-zinc-700 dark:text-zinc-300">Faster path to initial traction</p>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-5xl font-bold text-cyan-600 dark:text-cyan-400 mb-2">10k</h3>
                    <p className="text-zinc-700 dark:text-zinc-300">Guided journey to $10k MRR</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Tools Section - All tools in one place */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Subtle background */}
        <div className="absolute inset-0 -z-10 bg-zinc-50/80 dark:bg-zinc-900/50" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#e4e4e7_1px,transparent_1px),linear-gradient(to_bottom,#e4e4e7_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#27272a_1px,transparent_1px),linear-gradient(to_bottom,#27272a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        <div className="container px-4 md:px-6 relative">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-14 md:mb-16"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="secondary" className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground border-zinc-200 dark:border-zinc-700">
                Your toolkit
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
                All the <span className="gradient-text">AI tools</span> you need
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Stop paying for multiple AI tools. One subscription gives you the full toolkit to research your market, create content that converts, and launch your project.
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1, delayChildren: 0.1 },
                },
              }}
            >
              {/* Research */}
              <motion.div className="space-y-4" variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}>
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Database className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Research</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: Globe, title: "Analyze Website", desc: "Uncover content angles and positioning from any URL." },
                    { icon: Search, title: "Research Results", desc: "Product, audience, and keyword insights in one place." },
                    { icon: FileText, title: "Product research", desc: "Messaging and positioning tailored to your product." },
                    { icon: Users, title: "Audience research", desc: "Ideal customer profiles and where they hang out." },
                    { icon: Hash, title: "Keywords", desc: "Keyword opportunities and content gaps." },
                    { icon: Landmark, title: "Marketing Vault", desc: "Curated playbooks and templates." },
                  ].map((tool, i) => (
                    <Card key={i} className="group border-zinc-200 dark:border-zinc-800 bg-card/80 backdrop-blur-sm hover:shadow-md hover:border-primary/20 transition-all duration-200 rounded-xl overflow-hidden">
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                          <tool.icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <h4 className="font-semibold text-foreground text-sm md:text-base">{tool.title}</h4>
                          <p className="text-sm text-muted-foreground mt-0.5">{tool.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>

              {/* Content Engine */}
              <motion.div className="space-y-4" variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}>
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Zap className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Content Engine</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: ListTree, title: "Generate Content", desc: "AI content ideas and briefs from your research." },
                    { icon: FileText, title: "Content Library", desc: "All your generated content in one place." },
                    { icon: Calendar, title: "Social Calendar", desc: "Plan and schedule posts across platforms." },
                  ].map((tool, i) => (
                    <Card key={i} className="group border-zinc-200 dark:border-zinc-800 bg-card/80 backdrop-blur-sm hover:shadow-md hover:border-primary/20 transition-all duration-200 rounded-xl overflow-hidden">
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                          <tool.icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <h4 className="font-semibold text-foreground text-sm md:text-base">{tool.title}</h4>
                          <p className="text-sm text-muted-foreground mt-0.5">{tool.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>

              {/* Launch tools */}
              <motion.div className="space-y-4" variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}>
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Rocket className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Launch tools</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: FolderOpen, title: "Directories", desc: "Submit to launch directories in one flow." },
                    { icon: Target, title: "Product Hunt", desc: "Generate launch content and prepare for launch day." },
                    { icon: Mail, title: "Cold Outreach", desc: "Email templates and outreach strategies." },
                    { icon: Lightbulb, title: "Competitor content", desc: "Recreate and adapt competitor content." },
                  ].map((tool, i) => (
                    <Card key={i} className="group border-zinc-200 dark:border-zinc-800 bg-card/80 backdrop-blur-sm hover:shadow-md hover:border-primary/20 transition-all duration-200 rounded-xl overflow-hidden">
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                          <tool.icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <h4 className="font-semibold text-foreground text-sm md:text-base">{tool.title}</h4>
                          <p className="text-sm text-muted-foreground mt-0.5">{tool.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="mt-12 md:mt-14 text-center space-y-4"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
                Use them to find your audience, write what resonates, and launch where it matters. One subscription, one toolkit, so you can focus on building.
              </p>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-12 px-8 font-semibold shadow-lg hover:shadow-xl">
                <Link to="/auth/register">
                  Get the full toolkit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Core Features & Process Section - reduced top padding */}
      <section className="py-12 md:py-16">
        <div className="container px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Section header */}
            <div className="text-center mb-16">
              <motion.h2 className="text-3xl md:text-4xl font-bold mb-4" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }}>
                <div className="flex flex-wrap justify-center items-baseline">
                  <span className="relative inline-block mr-2">
                    <span className="gradient-text">Complete Marketing OS</span>
                    <svg className="absolute -bottom-4 left-0 right-0 w-full" height="10" viewBox="0 0 300 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 6C75 3 225 3 292 6" stroke="url(#paint0_linear)" strokeWidth="3" strokeLinecap="round"/>
                      <defs>
                        <linearGradient id="paint0_linear" x1="8" y1="6" x2="292" y2="6" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#4F46E5" stopOpacity="0"/>
                          <stop offset="0.5" stopColor="#4F46E5"/>
                          <stop offset="1" stopColor="#4F46E5" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  </span>
                  <span className="text-zinc-900 dark:text-white">for Builders</span>
                </div>
              </motion.h2>
              <motion.p className="text-lg text-zinc-600 dark:text-zinc-300 max-w-3xl mx-auto" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }}>
                A complete system that automates your marketing from research to content creation to proven launch strategies. No experience needed.
              </motion.p>
            </div>
            
            {/* Process flow */}
            <div className="relative">
              {/* Decorative gradient background */}
              <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 via-blue-50/30 to-blue-50/50 dark:from-blue-900/5 dark:via-blue-900/5 dark:to-blue-900/5 rounded-2xl -z-10 blur-sm"></div>
              
              {/* Step 1: Research & Analysis */}
              <motion.div className="relative mb-20 md:mb-32" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }}>
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-16">
                  {/* Step number */}
                  <div className="flex-shrink-0 relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-sky-500 text-white text-2xl font-bold shadow-lg transform transition-transform hover:scale-110">
                      1
                    </div>
                    <div className="hidden md:block absolute top-full left-1/2 transform -translate-x-1/2 w-0.5 h-16 bg-gradient-to-b from-blue-500 to-transparent"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 md:pt-3">
                    <h3 className="text-2xl font-bold mb-4 text-zinc-900 dark:text-white">Marketing Analysis & Strategy</h3>
                    <p className="text-zinc-600 dark:text-zinc-300 mb-6">We analyze your product, audience, and market to create a data-driven growth strategy uniquely tailored to you.</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 transition-all duration-300">
                            <Target className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Digital Presence Analyzer</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Advanced analysis of your website, social profiles, and positioning</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 transition-all duration-300">
                            <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Audience Match</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Identify ideal customer profiles and where they hang out online</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 transition-all duration-300">
                            <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Opportunity Finder</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Discover untapped marketing channels with high conversion potential</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-cyan-500/50 to-cyan-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-cyan-100 dark:bg-cyan-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-cyan-100/80 dark:ring-cyan-800/20 group-hover:shadow group-hover:bg-cyan-50 dark:group-hover:bg-cyan-900/40 transition-all duration-300">
                            <Compass className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300">Differentiator Mapping</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Identify your unique selling points vs competitors</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Step 2: Content Generation */}
              <motion.div className="relative mb-20 md:mb-32" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }}>
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-16">
                  {/* Step number */}
                  <div className="flex-shrink-0 relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-sky-500 text-white text-2xl font-bold shadow-lg transform transition-transform hover:scale-110">
                      2
                    </div>
                    <div className="hidden md:block absolute top-full left-1/2 transform -translate-x-1/2 w-0.5 h-16 bg-gradient-to-b from-blue-500 to-transparent"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 md:pt-3">
                    <h3 className="text-2xl font-bold mb-4 text-zinc-900 dark:text-white">AI Content Engine</h3>
                    <p className="text-zinc-600 dark:text-zinc-300 mb-6">Create conversion-optimized content for any platform in minutes, not hours. All with marketing expertise built-in.</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 transition-all duration-300">
                            <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Multi-format Creator</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Generate tweets, LinkedIn posts, emails, blogs - all from one brief</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 transition-all duration-300">
                            <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Content Calendar</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">30-day social content plans with publishing schedule</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 transition-all duration-300">
                            <Lightbulb className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Viral Templates</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">High-performing content structures based on what works</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-cyan-500/50 to-cyan-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-cyan-100 dark:bg-cyan-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-cyan-100/80 dark:ring-cyan-800/20 group-hover:shadow group-hover:bg-cyan-50 dark:group-hover:bg-cyan-900/40 transition-all duration-300">
                            <Clock className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300">One-Click Variants</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Create multiple versions in seconds for testing effectiveness</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Step 3: Content Management */}
              <motion.div className="relative" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }}>
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-16">
                  {/* Step number */}
                  <div className="flex-shrink-0 relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-pink-600 text-white text-2xl font-bold shadow-lg transform transition-transform hover:scale-110">
                      3
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 md:pt-3">
                    <h3 className="text-2xl font-bold mb-4 text-zinc-900 dark:text-white">AI Marketing Agents</h3>
                    <p className="text-zinc-600 dark:text-zinc-300 mb-6">Deploy autonomous AI agents that work 24/7 to find customers, create authentic content, and drive conversions while you sleep. The future of user acquisition is here.</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-500/50 to-red-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-orange-100/80 dark:ring-orange-800/20 group-hover:shadow group-hover:bg-gradient-to-br group-hover:from-orange-50 group-hover:to-red-50 dark:group-hover:from-orange-900/40 dark:group-hover:to-red-900/40 transition-all duration-300">
                            <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">Reddit AI Agent</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Discovers relevant communities and engages authentically to build trust</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-blue-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-gradient-to-br group-hover:from-blue-50 group-hover:to-blue-50 dark:group-hover:from-blue-900/40 dark:group-hover:to-blue-900/40 transition-all duration-300">
                            <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">X.com AI Agent</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Identifies prospects and sends personalized DMs that convert</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500/50 to-pink-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-gradient-to-br from-blue-100 to-pink-100 dark:from-blue-900/30 dark:to-pink-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-blue-100/80 dark:ring-blue-800/20 group-hover:shadow group-hover:bg-gradient-to-br group-hover:from-blue-50 group-hover:to-pink-50 dark:group-hover:from-blue-900/40 dark:group-hover:to-pink-900/40 transition-all duration-300">
                            <Bot className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Autonomous Operation</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Set once, run forever - agents learn and improve over time</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="group bg-white dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg p-5 border border-zinc-200/80 dark:border-zinc-700/80 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                        {/* Subtle gradient accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-emerald-500/50 to-teal-400/20 rounded-l"></div>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>
                        
                        <div className="flex items-start gap-3 relative">
                          <div className="bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-lg p-2.5 mt-1 shadow-sm ring-1 ring-emerald-100/80 dark:ring-emerald-800/20 group-hover:shadow group-hover:bg-gradient-to-br group-hover:from-emerald-50 group-hover:to-teal-50 dark:group-hover:from-emerald-900/40 dark:group-hover:to-teal-900/40 transition-all duration-300">
                            <Zap className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white mb-1.5 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300">Real-time Insights</h4>
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">Live performance tracking and optimization suggestions</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* CTA */}
            <motion.div className="mt-20 text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }}>
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12" asChild>
                <Link to="/features">
                  Explore All Features <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* AI Agents Spotlight Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-blue-900/20 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-[20%] left-[10%] w-72 h-72 bg-blue-400/20 rounded-full blur-3xl" 
            animate={{ y: [0, 50, 0], scale: [1, 1.1, 1] }} 
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-[20%] right-[15%] w-80 h-80 bg-blue-400/20 rounded-full blur-3xl" 
            animate={{ y: [0, -60, 0], scale: [1, 1.2, 1] }} 
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 5 }}
          />
        </div>

        <div className="container px-4 md:px-6 relative">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
              }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-100 dark:from-blue-900/50 dark:to-blue-900/50 border border-blue-200 dark:border-blue-700/50 mb-6">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">NEW: Marketing Agents Available</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Meet Your New <br />
                <span className="bg-gradient-to-r gradient-text">
                  AI Marketing Team
                </span>
              </h2>
              
              <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-300 max-w-4xl mx-auto leading-relaxed">
                Stop manually hunting for users. Our AI agents work 24/7 to find your ideal customers, create engaging content, and convert prospects into users, all while you sleep.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 mb-16">
              {/* Reddit Agent Card */}
              <motion.div
                className="group bg-white dark:bg-zinc-800/90 backdrop-blur-sm rounded-2xl p-8 border border-zinc-200 dark:border-zinc-700 shadow-lg hover:shadow-2xl transition-all duration-500"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={{
                  hidden: { opacity: 0, x: -50 },
                  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
                }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/50 dark:to-red-900/50 rounded-2xl">
                    <svg className="h-10 w-10 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">Reddit AI Agent</h3>
                    <p className="text-orange-600 font-medium">Community Discovery & Engagement</p>
                  </div>
                </div>
                
                <p className="text-zinc-600 dark:text-zinc-300 mb-6">
                  Automatically finds relevant subreddits, generates authentic posts, and engages with your target communities, all while following Reddit's best practices.
                </p>
                
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Finds perfect subreddits for your business</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Creates Reddit-optimized content</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Posts at optimal engagement times</span>
                  </div>
                </div>
                
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800/30">
                  <p className="text-sm text-orange-700 dark:text-orange-300 italic">
                    "Just launched a productivity tool for developers. Anyone else struggling with context switching? Here's how I solved it..."
                  </p>
                </div>
              </motion.div>

              {/* X.com Agent Card */}
              <motion.div
                className="group bg-white dark:bg-zinc-800/90 backdrop-blur-sm rounded-2xl p-8 border border-zinc-200 dark:border-zinc-700 shadow-lg hover:shadow-2xl transition-all duration-500"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={{
                  hidden: { opacity: 0, x: 50 },
                  visible: { opacity: 1, x: 0, transition: { duration: 0.7, delay: 0.2 } }
                }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-900/50 dark:to-blue-900/50 rounded-2xl">
                    <svg className="h-10 w-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">X.com AI Agent</h3>
                    <p className="text-blue-600 font-medium">Smart DM & Conversion</p>
                  </div>
                </div>
                
                <p className="text-zinc-600 dark:text-zinc-300 mb-6">
                  Identifies potential users through their tweets, sends personalized DMs that feel human, and converts prospects into loyal customers.
                </p>
                
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Finds ideal prospects via tweet analysis</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Sends human-like personalized DMs</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Follows up intelligently based on responses</span>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/30">
                  <p className="text-sm text-blue-700 dark:text-blue-300 italic">
                    "Hi Sarah! Saw your tweet about struggling with project management. I built something that might help - mind if I share it?"
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Call to Action */}
            <motion.div 
              className="text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.4 } }
              }}
            >
              <Button 
                size="lg" 
                className="group relative overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-6 text-lg font-semibold shadow-xl"
                asChild
              >
                <Link to="/ai-agents">
                  Discover AI Agents
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-4">
                See how AI agents can automate your user acquisition
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Business Impact & Measurable Benefits Section */}
      <section className="py-20 md:py-28">
        <div className="container px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Section header */}
            <div className="text-center mb-16">
              <motion.h2 className="text-3xl md:text-4xl font-bold mb-4" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }}>
                Transform Your Marketing – <span className="gradient-text">Measurable Results</span>
              </motion.h2>
              <motion.p className="text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }}>
                LaunchGuide delivers concrete, measurable improvements to your marketing efforts.
              </motion.p>
            </div>
            
            {/* Stats grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Stat 1 */}
              <motion.div className="bg-white dark:bg-zinc-800 rounded-xl overflow-hidden shadow-sm border border-zinc-200 dark:border-zinc-700" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} whileHover={{
              y: -5,
              transition: {
                duration: 0.2
              }
            }}>
                <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-500"></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      Resource Efficiency
                    </div>
                  </div>
                  
                  <h3 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-2">
                    70-80<span className="text-blue-600 dark:text-blue-400">%</span>
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-300">
                    Reduction in time spent on marketing activities
                  </p>
                </div>
              </motion.div>
              
              {/* Stat 2 */}
              <motion.div className="bg-white dark:bg-zinc-800 rounded-xl overflow-hidden shadow-sm border border-zinc-200 dark:border-zinc-700" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} whileHover={{
              y: -5,
              transition: {
                duration: 0.2
              }
            }}>
                <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-500"></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                      <Rocket className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      Go-to-Market Speed
                    </div>
                  </div>
                  
                  <h3 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-2">
                    3-5<span className="text-blue-600 dark:text-blue-400">x</span>
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-300">Faster time from launch day to active user acquisition</p>
                </div>
              </motion.div>
              
              {/* Stat 3 */}
              <motion.div className="bg-white dark:bg-zinc-800 rounded-xl overflow-hidden shadow-sm border border-zinc-200 dark:border-zinc-700" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} whileHover={{
              y: -5,
              transition: {
                duration: 0.2
              }
            }}>
                <div className="h-2 bg-gradient-to-r from-blue-500 to-pink-500"></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                      <Layers className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      Content Production
                    </div>
                  </div>
                  
                  <h3 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-2">
                    30<span className="text-blue-600 dark:text-blue-400">+</span>
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-300">
                    Days of consistent content from a single generation session
                  </p>
                </div>
              </motion.div>
            </div>
            
            {/* Additional benefits */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left column */}
              <motion.div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 rounded-xl p-8 border border-blue-100 dark:border-blue-900/20 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }}>
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-200/30 dark:bg-blue-700/20 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-20 h-20 bg-blue-200/30 dark:bg-blue-700/20 rounded-full blur-xl"></div>
                
                <h3 className="text-2xl font-bold mb-4 text-zinc-900 dark:text-white relative z-10">Quality & Consistency</h3>
                
                <ul className="space-y-4 relative z-10">
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-1 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900 dark:text-white">Consistent Messaging</h4>
                      <p className="text-zinc-600 dark:text-zinc-300 text-sm">Ensures product messaging remains coherent across all platforms</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-1 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900 dark:text-white">Higher Quality Output</h4>
                      <p className="text-zinc-600 dark:text-zinc-300 text-sm">Produces professional marketing materials regardless of your marketing expertise</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-1 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900 dark:text-white">Multi-Channel Presence</h4>
                      <p className="text-zinc-600 dark:text-zinc-300 text-sm">Maintain a professional presence across all relevant platforms simultaneously</p>
                    </div>
                  </li>
                </ul>
              </motion.div>
              
              {/* Right column */}
              <motion.div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 rounded-xl p-8 border border-blue-100 dark:border-blue-900/20 relative overflow-hidden" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }}>
                {/* Decorative elements */}
                <div className="absolute top-0 left-0 -mt-4 -ml-4 w-24 h-24 bg-blue-200/30 dark:bg-blue-700/20 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 right-0 -mb-4 -mr-4 w-20 h-20 bg-blue-200/30 dark:bg-blue-700/20 rounded-full blur-xl"></div>
                
                <h3 className="text-2xl font-bold mb-4 text-zinc-900 dark:text-white relative z-10">Cost & Resource Savings</h3>
                
                <ul className="space-y-4 relative z-10">
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-1 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900 dark:text-white">Tool Consolidation</h4>
                      <p className="text-zinc-600 dark:text-zinc-300 text-sm">Eliminates the need for multiple specialized marketing tools and services</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-1 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900 dark:text-white">No Marketing Hires</h4>
                      <p className="text-zinc-600 dark:text-zinc-300 text-sm">Launch and scale your marketing without hiring dedicated marketing staff</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-1 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900 dark:text-white">Focus on Core Strengths</h4>
                      <p className="text-zinc-600 dark:text-zinc-300 text-sm">Spend more time building your product while AI handles your marketing</p>
                    </div>
                  </li>
                </ul>
              </motion.div>
            </div>
            
            {/* CTA */}
            <motion.div className="mt-16 text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }}>
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12" asChild>
                <Link to="/auth/register">
                  Start Transforming Your Marketing <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Footer Banner */}
      <div className="container px-4 md:px-6 mb-20">
        <motion.div className="relative bg-gradient-to-r from-blue-600 to-sky-500 rounded-2xl p-8 md:p-12 overflow-hidden shadow-xl" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }}>
            {/* Grid background pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="footer-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M0 0 L40 0 L40 40 L0 40 Z" fill="none" stroke="white" strokeWidth="1" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#footer-grid)" />
              </svg>
            </div>
            
            <div className="relative z-10 text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">Ready to Transform Your Marketing?</h2>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto mb-8">
                Join thousands of technical founders and product teams who are using LaunchGuide to automate their marketing and accelerate user acquisition.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 h-12 text-lg font-medium" asChild>
                  <Link to="/auth/register">
                    Get Started Free
                  </Link>
                </Button>
                
                <Button size="lg" className="bg-blue-700 text-white hover:bg-blue-800 px-8 h-12 text-lg font-medium border border-white" asChild>
                  <Link to="/features">
                    See All Features
                  </Link>
                </Button>
              </div>
              
              <p className="text-blue-100 mt-6 text-sm">
                No credit card required. Free plan available.
              </p>
            </div>
          </motion.div>
        </div>
        
        {/* Footer Section - Footer component is imported elsewhere */}
      
      <Footer />
    </div>
  );
};

// Tabs component for user types
const TabsDemo = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = [{
    title: "Vibe Coders",
    icon: <Code className="h-5 w-5" />,
    content: {
      challenges: ["Limited time to focus on marketing", "No dedicated marketing team", "Technical expertise but limited marketing knowledge"],
      benefits: ["80% reduction in time spent on marketing activities", "Complete marketing playbook tailored to your product", "Technical-first approach that speaks your language"],
      useCase: "A solo developer who built a SaaS tool needs to attract users but doesn't have time to learn marketing from scratch. LaunchGuide analyzes their product, creates a tailored marketing strategy, and generates ready-to-use content across multiple platforms."
    }
  }, {
    title: "Small Product Teams",
    icon: <Users className="h-5 w-5" />,
    content: {
      challenges: ["Limited marketing resources", "Need for consistent messaging across channels", "Balancing product development with marketing needs"],
      benefits: ["Unified marketing approach for the entire team", "Consistent brand voice across all platforms", "Scalable content creation that grows with your team"],
      useCase: "A 5-person product team has built an innovative tool but can't afford a dedicated marketing department. LaunchGuide provides a centralized platform for their marketing efforts, ensuring consistent messaging and allowing team members to collaborate efficiently."
    }
  }, {
    title: "Product Managers",
    icon: <Layers className="h-5 w-5" />,
    content: {
      challenges: ["Communicating product value effectively", "Coordinating marketing efforts with development", "Gathering market insights to inform product decisions"],
      benefits: ["Data-driven insights about your target audience", "Seamless integration of product and marketing timelines", "Effective communication of complex product features"],
      useCase: "A product manager needs to launch a new feature and communicate its value to users. LaunchGuide analyzes the feature, identifies the key benefits for different user segments, and creates targeted content that highlights the value proposition in user-centric language."
    }
  }, {
    title: "Engineering-Led Companies",
    icon: <Target className="h-5 w-5" />,
    content: {
      challenges: ["Translating technical excellence into marketing messages", "Building a marketing culture in an engineering-focused environment", "Attracting technical users with authentic content"],
      benefits: ["Marketing that respects the technical depth of your product", "Content that resonates with technical audiences", "Balanced approach between technical detail and user benefits"],
      useCase: "An engineering-led company has built a powerful developer tool with advanced capabilities, but struggles to communicate its value. LaunchGuide creates technically accurate yet accessible content that respects the product's complexity while clearly articulating its benefits."
    }
  }];
  return (
    <div className="w-full">
      {/* Tab navigation */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {tabs.map((tab, index) => <motion.button key={index} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === index ? "bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-md" : "bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700"}`} onClick={() => setActiveTab(index)} whileHover={{
        y: -2
      }} whileTap={{
        y: 0
      }}>
            <span className={activeTab === index ? "text-white" : "text-blue-600 dark:text-blue-400"}>
              {tab.icon}
            </span>
            {tab.title}
          </motion.button>)}
      </div>
      
      {/* Tab content */}
      <motion.div className="bg-white dark:bg-zinc-800 rounded-xl p-8 shadow-sm border border-zinc-200 dark:border-zinc-700" key={activeTab} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }}>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-4 text-zinc-900 dark:text-white flex items-center gap-2">
              <span className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                {tabs[activeTab].icon}
              </span>
              {tabs[activeTab].title}
            </h3>
            
            <div className="mb-6">
              <h4 className="font-medium text-zinc-900 dark:text-white mb-2">Challenges:</h4>
              <ul className="space-y-2">
                {tabs[activeTab].content.challenges.map((challenge, i) => <li key={i} className="flex items-start gap-2 text-zinc-600 dark:text-zinc-300">
                    <div className="mt-1 text-red-500 dark:text-red-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m15 9-6 6"></path>
                        <path d="m9 9 6 6"></path>
                      </svg>
                    </div>
                    {challenge}
                  </li>)}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-zinc-900 dark:text-white mb-2">Benefits:</h4>
              <ul className="space-y-2">
                {tabs[activeTab].content.benefits.map((benefit, i) => <li key={i} className="flex items-start gap-2 text-zinc-600 dark:text-zinc-300">
                    <div className="mt-1 text-green-500 dark:text-green-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                    {benefit}
                  </li>)}
              </ul>
            </div>
          </div>
          
          <div className="bg-zinc-50 dark:bg-zinc-900/50 rounded-lg p-6 border border-zinc-200 dark:border-zinc-700">
            <h4 className="font-medium text-zinc-900 dark:text-white mb-3">Use Case Scenario:</h4>
            <p className="text-zinc-600 dark:text-zinc-300">
              {tabs[activeTab].content.useCase}
            </p>
            <div className="mt-6">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground w-full" asChild>
                <Link to="/auth/register">
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
export default LandingPage;